import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Switch } from '../../ui/switch';
import { Checkbox } from '../../ui/checkbox';
import { Loader2, ChevronDown, X } from 'lucide-react';
import {
  fetchFrontInboxes,
  fetchFrontChannels,
  fetchEmailSignatures,
  fetchInboxChannels,
  fetchTeammateEmails,
  fetchExcludedEmails,
} from '../../../api/suggestionService';
import { reassignSubmission } from '../../../api/submissionService';

const ReassignModal = ({ submission, userEmail, isDark, onClose, onSuccess }) => {
  // Derive initial values synchronously from submission prop
  const _isMe = (submission.email || '').toLowerCase() === (userEmail || '').toLowerCase();

  const [formData, setFormData] = useState({
    sendingOption: _isMe ? 'sending-from-me' : 'sending-from-other',
    email: submission.email || '',
    channel_id: submission.channel_id || '',
    channel_send_as: submission.channel_send_as || '',
    inbox_id: submission.inbox_id || '',
    signature_id: submission.signature_id || '',
  });

  // Email autocomplete
  const [showEmailField, setShowEmailField] = useState(!_isMe);
  const [teammateEmails, setTeammateEmails] = useState([]);
  const [excludedEmails, setExcludedEmails] = useState([]);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const emailSuggestionsRef = useRef(null);

  // Signatures
  const [showSignatureSelect, setShowSignatureSelect] = useState(false);
  const [isLoadingSignatures, setIsLoadingSignatures] = useState(false);
  const [emailSignatures, setEmailSignatures] = useState([]);

  // Channel / Inbox
  const [allInboxes, setAllInboxes] = useState([]);
  const [allChannels, setAllChannels] = useState([]);
  const [inboxChannels, setInboxChannels] = useState([]);
  const [isLoadingInboxes, setIsLoadingInboxes] = useState(false);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [isDefaultChannel, setIsDefaultChannel] = useState(!!submission.inbox_id);
  const [channelInput, setChannelInput] = useState(submission.channel_send_as || '');
  const [channelSuggestions, setChannelSuggestions] = useState([]);
  const [showChannelSuggestions, setShowChannelSuggestions] = useState(false);
  const [selectedChannelSuggestionIndex, setSelectedChannelSuggestionIndex] = useState(-1);
  const channelSuggestionsRef = useRef(null);

  // Inbox name/send_as for saving
  const [selectedInboxName, setSelectedInboxName] = useState(submission.inbox_name || '');
  const [selectedInboxSendAs, setSelectedInboxSendAs] = useState(submission.inbox_send_as || '');

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [resultState, setResultState] = useState(null); // { type: 'success'|'error', message: '' }

  const setDefaultChannel = useCallback((channels, preferredChannelId = null) => {
    if (!channels || channels.length === 0) return;

    const withSendAs = channels.filter(c => c.send_as);
    if (withSendAs.length === 0) return;

    // Prefer the previously-saved channel_id if provided
    let defaultChannel = null;
    if (preferredChannelId) {
      defaultChannel = withSendAs.find(c => c.id === preferredChannelId) || null;
    }

    if (!defaultChannel) {
      const priorities = [
        'jamesons.com.au',
        'cngproperty.com.au',
        'stratalogic.com.au',
        'cnginsuranceservices.com.au',
      ];
      for (const domain of priorities) {
        defaultChannel = withSendAs.find(c => c.send_as?.toLowerCase().includes(domain));
        if (defaultChannel) break;
      }
    }

    if (!defaultChannel) defaultChannel = withSendAs[0];

    setChannelInput(defaultChannel.name);
    setFormData(prev => ({
      ...prev,
      channel_id: defaultChannel.id,
      channel_send_as: defaultChannel.send_as,
    }));
    setErrors(prev => ({ ...prev, channel: false, channelSuggestion: false }));
  }, []);

  const fetchAndApplySignatures = useCallback(async (email, currentSignatureId, excluded) => {
    const isExcluded = excluded.some(e => e.email?.toLowerCase() === email.toLowerCase());
    if (!isExcluded) {
      setShowSignatureSelect(false);
      setEmailSignatures([]);
      setFormData(prev => ({ ...prev, signature_id: '' }));
      return;
    }

    setIsLoadingSignatures(true);
    try {
      const sigs = await fetchEmailSignatures(email);
      setEmailSignatures(sigs || []);
      if (sigs && sigs.length > 1) {
        setShowSignatureSelect(true);
        // Keep the existing signature_id only if it exists in the new list
        const exists = sigs.some(s => String(s.id) === String(currentSignatureId));
        setFormData(prev => ({ ...prev, signature_id: exists ? currentSignatureId : '' }));
      } else if (sigs && sigs.length === 1) {
        setShowSignatureSelect(false);
        setFormData(prev => ({ ...prev, signature_id: String(sigs[0].id) }));
      } else {
        setShowSignatureSelect(false);
        setFormData(prev => ({ ...prev, signature_id: '' }));
      }
    } catch {
      setEmailSignatures([]);
      setShowSignatureSelect(false);
      setFormData(prev => ({ ...prev, signature_id: '' }));
    } finally {
      setIsLoadingSignatures(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const email = submission.email || '';
      const hadInbox = !!submission.inbox_id;

      // Fetch teammate/excluded emails + inboxes/channels in parallel
      const [emails, excluded, inboxes, channels] = await Promise.all([
        fetchTeammateEmails(),
        fetchExcludedEmails(),
        fetchFrontInboxes(email),
        fetchFrontChannels(email),
      ]);

      setTeammateEmails(emails);
      setExcludedEmails(excluded);
      setAllInboxes(inboxes || []);
      setAllChannels(channels || []);

      if (hadInbox && submission.inbox_id) {
        const iChans = await fetchInboxChannels(submission.inbox_id);
        setInboxChannels(iChans || []);
        // Update channelInput with the actual channel name once loaded
        const match = (iChans || []).find(c => c.id === submission.channel_id);
        if (match) setChannelInput(match.name);
      } else {
        // Update channelInput with the actual channel name once loaded
        const match = (channels || []).find(c => c.id === submission.channel_id);
        if (match) setChannelInput(match.name);
      }

      await fetchAndApplySignatures(email, submission.signature_id || '', excluded);
      setIsInitializing(false);
    };

    init();
  }, []); // run once on mount

  // disable body scroll while modal is open

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Close email suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (emailSuggestionsRef.current && !emailSuggestionsRef.current.contains(e.target)) {
        setShowEmailSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close channel suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (channelSuggestionsRef.current && !channelSuggestionsRef.current.contains(e.target)) {
        setShowChannelSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);


  const handleSendingOptionChange = async (value) => {
    setChannelInput('');
    setIsDefaultChannel(false);
    setErrors(prev => ({ ...prev, channel: false, channelSuggestion: false }));
    setAllInboxes([]);
    setAllChannels([]);
    setInboxChannels([]);
    setEmailSignatures([]);
    setShowSignatureSelect(false);
    setSelectedInboxName('');
    setSelectedInboxSendAs('');

    if (value === 'sending-from-other') {
      setShowEmailField(true);
      setFormData(prev => ({
        ...prev,
        sendingOption: value,
        email: '',
        channel_id: '',
        channel_send_as: '',
        inbox_id: '',
        signature_id: '',
      }));
    } else {
      // sending-from-me
      setShowEmailField(false);
      setFormData(prev => ({
        ...prev,
        sendingOption: value,
        email: userEmail,
        channel_id: '',
        channel_send_as: '',
        inbox_id: '',
        signature_id: '',
      }));

      setIsLoadingInboxes(true);
      setIsLoadingChannels(true);

      const [inboxes, channels] = await Promise.all([
        fetchFrontInboxes(userEmail),
        fetchFrontChannels(userEmail),
      ]);

      setAllInboxes(inboxes || []);
      setAllChannels(channels || []);
      setIsLoadingInboxes(false);
      setIsLoadingChannels(false);
      setDefaultChannel(channels || []);

      await fetchAndApplySignatures(userEmail, '', excludedEmails);
    }
  };

  // Called as user types in the email input
  const handleEmailSearch = (value) => {
    setAllInboxes([]);
    setAllChannels([]);
    setInboxChannels([]);
    setChannelInput('');
    setEmailSignatures([]);
    setShowSignatureSelect(false);
    setFormData(prev => ({
      ...prev,
      email: value,
      channel_id: '',
      channel_send_as: '',
      inbox_id: '',
      signature_id: '',
    }));
    setErrors(prev => ({ ...prev, emailSuggestion: true }));

    if (value.length >= 2) {
      const filtered = teammateEmails.filter(e => e.toLowerCase().includes(value.toLowerCase()));
      setEmailSuggestions(filtered);
      setShowEmailSuggestions(filtered.length > 0);
      setSelectedSuggestionIndex(-1);
    } else {
      setEmailSuggestions([]);
      setShowEmailSuggestions(false);
    }
  };

  // Called when user picks an email from the dropdown
  const handleEmailSelect = async (email) => {
    setEmailSuggestions([]);
    setShowEmailSuggestions(false);
    setErrors(prev => ({ ...prev, emailSuggestion: false }));
    setFormData(prev => ({ ...prev, email, channel_id: '', channel_send_as: '', inbox_id: '', signature_id: '' }));

    setIsLoadingInboxes(true);
    setIsLoadingChannels(true);
    setChannelInput('');
    setAllInboxes([]);
    setAllChannels([]);
    setInboxChannels([]);
    setIsDefaultChannel(false);
    setSelectedInboxName('');
    setSelectedInboxSendAs('');

    const [inboxes, channels] = await Promise.all([
      fetchFrontInboxes(email),
      fetchFrontChannels(email),
    ]);

    setAllInboxes(inboxes || []);
    setAllChannels(channels || []);
    setIsLoadingInboxes(false);
    setIsLoadingChannels(false);
    setDefaultChannel(channels || []);

    await fetchAndApplySignatures(email, '', excludedEmails);
  };

  const handleEmailKeyDown = (e) => {
    if (!showEmailSuggestions) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.min(prev + 1, emailSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleEmailSelect(emailSuggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowEmailSuggestions(false);
    }
  };

  const handleChannelInputChange = (e) => {
    const value = e.target.value;
    setChannelInput(value);
    if (value.length >= 1) {
      const filtered = allChannels.filter(
        c => c.send_as && (
          c.name.toLowerCase().includes(value.toLowerCase()) ||
          c.send_as.toLowerCase().includes(value.toLowerCase())
        )
      );
      setChannelSuggestions(filtered);
      setShowChannelSuggestions(filtered.length > 0);
      setSelectedChannelSuggestionIndex(-1);
    } else {
      setChannelSuggestions([]);
      setShowChannelSuggestions(false);
    }
  };

  const handleInboxChannelInputChange = (e) => {
    const value = e.target.value;
    setChannelInput(value);
    if (value.length >= 1) {
      const filtered = inboxChannels.filter(
        c => c.send_as && (
          c.name.toLowerCase().includes(value.toLowerCase()) ||
          c.send_as.toLowerCase().includes(value.toLowerCase())
        )
      );
      setChannelSuggestions(filtered);
      setShowChannelSuggestions(filtered.length > 0);
      setSelectedChannelSuggestionIndex(-1);
    } else {
      setChannelSuggestions([]);
      setShowChannelSuggestions(false);
    }
  };

  const handleChannelInputKeyDown = (e) => {
    if (!showChannelSuggestions) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedChannelSuggestionIndex(prev => Math.min(prev + 1, channelSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedChannelSuggestionIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && selectedChannelSuggestionIndex >= 0) {
      e.preventDefault();
      handleChannelSuggestionClick(channelSuggestions[selectedChannelSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowChannelSuggestions(false);
    }
  };

  const handleChannelSuggestionClick = (channel) => {
    setChannelInput(channel.name);
    setFormData(prev => ({
      ...prev,
      channel_id: channel.id,
      channel_send_as: channel.send_as,
    }));
    setShowChannelSuggestions(false);
    setErrors(prev => ({ ...prev, channel: false, channelSuggestion: false }));
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.channel_id) {
      setErrors(prev => ({
        ...prev,
        email: !formData.email,
        channel: !formData.channel_id,
      }));
      return;
    }

    setIsSubmitting(true);
    try {
      await reassignSubmission(submission.id, {
        email: formData.email,
        channel_id: formData.channel_id,
        channel_send_as: formData.channel_send_as || null,
        inbox_id: formData.inbox_id || '',
        inbox_name: selectedInboxName || null,
        inbox_send_as: selectedInboxSendAs || null,
        signature_id: formData.signature_id || null,
      });

      onSuccess();
      setResultState({ type: 'success', message: 'Submission has been reassigned successfully.' });
    } catch (error) {
      setResultState({ type: 'error', message: error?.response?.data?.message || 'Failed to reassign submission.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const labelClass = `text-[16px] font-semibold transition-colors duration-200 ${isDark ? 'text-white' : 'text-[#072837]'}`;
  const inputClass = `w-full px-[12px] py-[12px] rounded-[8px] text-[16px] focus:outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] transition-colors duration-200 ${isDark
      ? 'bg-[#161515] text-white placeholder:text-[#b3b3b3] border-none'
      : 'bg-[#f5f5f5] border-none text-[#072837] placeholder:text-[#686868]'
    }`;
  const suggestionListClass = `absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#ced4da]'
    }`;
  const channelInputClass = `w-full px-[12px] py-[12px] pr-[40px] rounded-[8px] text-[16px] focus:outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] disabled:opacity-50 transition-colors duration-200 ${isDark
      ? 'bg-[#000000] text-white placeholder:text-[#b3b3b3] border-none'
      : 'bg-white text-[#072837] placeholder:text-[#686868] border-none'
    }`;
  if (resultState) {
    const isSuccess = resultState.type === 'success';
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" onClick={onClose} />
        <div className={`relative z-10 flex flex-col items-center w-full max-w-[380px] mx-[16px] rounded-[16px] overflow-hidden shadow-[0px_20px_60px_rgba(0,0,0,0.25)] ${isDark ? 'bg-[#0d0d0d]' : 'bg-white'}`}>
          <div className="flex flex-col items-center gap-[12px] px-[32px] pt-[32px] pb-[28px] w-full">
          
            <div className={`w-[64px] h-[64px] rounded-full flex items-center justify-center ${isSuccess ? 'bg-[#e8f5e9]' : 'bg-[#fdecea]'}`}>
              <img
                src={isSuccess ? '/assets/icons/completed.svg' : '/assets/icons/error.svg'}
                alt={isSuccess ? 'Success' : 'Error'}
                className="w-[36px] h-[36px]"
              />
            </div>

            <p className={`font-bold text-[18px] text-center m-0 ${isDark ? 'text-white' : 'text-[#072837]'}`}>
              {isSuccess ? 'Reassigned Successfully' : 'Reassignment Failed'}
            </p>

            <p className={`font-normal text-[14px] text-center m-0 leading-[1.5] ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}>
              {resultState.message}
            </p>

            <div className={`w-full h-[1px] my-[4px] ${isDark ? 'bg-[#292929]' : 'bg-[#f0f0f0]'}`} />

            <button
              onClick={onClose}
              className="px-[32px] py-[10px] rounded-[10px] text-[15px] font-semibold border-0 cursor-pointer transition-colors duration-200 bg-[#cd6d2c] text-white hover:bg-[#b85e25]"
            >
              Close
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      <div className={`relative z-10 w-full max-w-[750px] mx-[16px] rounded-[16px] shadow-2xl overflow-y-auto max-h-[90vh] transition-colors duration-200 ${isDark ? 'bg-[#000000]' : 'bg-white'
        }`}>
        <div className={`sticky top-0 z-[10] flex items-center justify-between px-[24px] py-[20px] rounded-t-[16px] ${isDark ? 'bg-[#000000]' : 'bg-white'}`}>
          <h2 className={`text-[18px] font-bold m-0 ${isDark ? 'text-white' : 'text-[#072837]'}`}>
            Reassign Submission
          </h2>
          <button
            onClick={onClose}
            className={`p-[4px] rounded-[6px] border-0 cursor-pointer transition-colors duration-200 bg-transparent ${isDark ? 'text-[#b3b3b3] hover:text-white' : 'text-[#686868] hover:text-[#072837]'
              }`}
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-[24px] pb-[20px] flex flex-col gap-[20px]">

          <div className="flex flex-col gap-[12px]">
            <div className={labelClass}>Add email address of Manager/Assist <span className="text-[#FF0000]">*</span></div>
            <RadioGroup
              value={formData.sendingOption}
              onValueChange={handleSendingOptionChange}
              className="flex gap-[24px]"
            >
              <label className={`flex items-center gap-[8px] cursor-pointer ${isDark ? 'text-white' : 'text-[#072837]'}`}>
                <RadioGroupItem value="sending-from-me" id="reassign-sending-from-me" />
                <span className="text-[16px] font-normal">Sending from Me</span>
              </label>
              <label className={`flex items-center gap-[8px] cursor-pointer ${isDark ? 'text-white' : 'text-[#072837]'}`}>
                <RadioGroupItem value="sending-from-other" id="reassign-sending-from-other" />
                <span className="text-[16px] font-normal">Sending from Others</span>
              </label>
            </RadioGroup>
          </div>

          {/* Email input (sending-from-other only) */}
          {showEmailField && (
            <div className="relative" ref={emailSuggestionsRef}>
              <input
                className={inputClass}
                type="text"
                placeholder="Enter email"
                value={formData.email}
                onChange={(e) => handleEmailSearch(e.target.value)}
                onKeyDown={handleEmailKeyDown}
                autoComplete="off"
              />
              {showEmailSuggestions && emailSuggestions.length > 0 && (
                <div className={suggestionListClass}>
                  {emailSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className={`px-3 py-2 text-[16px] cursor-pointer transition-colors duration-200 ${index === selectedSuggestionIndex
                          ? isDark ? 'bg-[#cd6d2c] text-[#000]' : 'bg-[#e9ecef] text-[#072837]'
                          : isDark ? 'bg-[#000000] hover:bg-[#292929] text-white' : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                        }`}
                      onMouseDown={() => handleEmailSelect(suggestion)}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Signature selector */}
          {showSignatureSelect && (
            <div className="flex flex-col gap-[12px]">
              <div className={labelClass}>Select Email Signature</div>
              {isLoadingSignatures ? (
                <div className={`text-[16px] ${isDark ? 'text-white' : 'text-[#072837]'}`}>Loading signatures...</div>
              ) : (
                <RadioGroup
                  value={formData.signature_id || ''}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, signature_id: value }))}
                  className="flex flex-wrap gap-[12px]"
                >
                  {emailSignatures.map((sig) => (
                    <label
                      key={sig.id}
                      className={`flex items-center gap-[8px] px-[12px] py-[10px] rounded-[8px] cursor-pointer transition-colors duration-200 ${isDark ? 'bg-[#000000] text-white hover:bg-[#292929]' : 'bg-[#f5f5f5] text-[#072837] hover:bg-[#e9ecef]'
                        }`}
                    >
                      <RadioGroupItem value={String(sig.id)} id={`reassign-sig-${sig.id}`} />
                      <span className="text-[16px] font-normal">{sig.name || `Signature ${sig.id}`}</span>
                    </label>
                  ))}
                </RadioGroup>
              )}
            </div>
          )}

          {/* Channel / Inbox section */}
          <div className={`rounded-[10px] p-[20px] flex flex-col gap-[16px] transition-colors duration-200 ${isDark ? 'bg-[#161515]' : 'bg-[#F5F5F5]'
            }`}>
            {isInitializing ? (
              <div className="flex items-center justify-center py-[32px]">
                <Loader2 className="h-[28px] w-[28px] animate-spin text-[#cd6d2c]" />
              </div>
            ) : <>
            {/* Default Email / Shared Inbox toggle — only when inboxes exist */}
            {allInboxes.length > 0 && (
              <>
                <div className={labelClass}>Please choose if you want to use default email or shared inbox</div>
                <div className={`flex items-center gap-[16px] p-[16px] rounded-[8px] w-fit ${isDark ? 'bg-[#000000]' : 'bg-[#ffffff]'}`}>
                  <span className={`text-[16px] font-normal ${isDark ? 'text-white' : 'text-[#072837]'}`}>Default Email</span>
                  <Switch
                    checked={isDefaultChannel}
                    onCheckedChange={(checked) => {
                      setIsDefaultChannel(checked);
                      setChannelInput('');
                      setFormData(prev => ({ ...prev, channel_id: '', channel_send_as: '', inbox_id: '' }));
                      setSelectedInboxName('');
                      setSelectedInboxSendAs('');
                      if (!checked) {
                        setDefaultChannel(allChannels);
                      }
                    }}
                  />
                  <span className={`text-[16px] font-normal ${isDark ? 'text-white' : 'text-[#072837]'}`}>Shared Inbox</span>
                </div>
              </>
            )}

            {/* Shared Inbox selection */}
            {isDefaultChannel && (
              <div className="flex flex-col gap-[16px]">
                <div className={labelClass}>Select Shared Inbox</div>
                {isLoadingInboxes ? (
                  <div className={`text-[16px] ${isDark ? 'text-white' : 'text-[#072837]'}`}>Loading inboxes...</div>
                ) : allInboxes.filter(i => i.send_as).length === 0 ? (
                  <div className={`text-[16px] ${isDark ? 'text-white' : 'text-[#072837]'}`}>No shared inboxes</div>
                ) : (
                  <div className="flex flex-col gap-[16px]">
                    {allInboxes.filter(i => i.send_as).map((inbox) => (
                      <div key={inbox.id} className="flex flex-col gap-[12px]">
                        <div className="flex items-center gap-[8px]">
                          <Checkbox
                            id={`reassign-inbox-${inbox.id}`}
                            isDark={isDark}
                            checked={formData.inbox_id === inbox.id}
                            onCheckedChange={(checked) => {
                              setInboxChannels([]);
                              if (checked) {
                                setFormData(prev => ({ ...prev, inbox_id: inbox.id, channel_id: '', channel_send_as: '' }));
                                setSelectedInboxName(inbox.name || '');
                                setSelectedInboxSendAs(inbox.send_as || '');
                                setChannelInput('');
                                setIsLoadingChannels(true);
                                fetchInboxChannels(inbox.id).then(channels => {
                                  setInboxChannels(channels || []);
                                  setDefaultChannel(channels || []);
                                  setIsLoadingChannels(false);
                                });
                              } else {
                                setFormData(prev => ({ ...prev, inbox_id: '', channel_id: '', channel_send_as: '' }));
                                setSelectedInboxName('');
                                setSelectedInboxSendAs('');
                              }
                            }}
                          />
                          <label htmlFor={`reassign-inbox-${inbox.id}`} className={`text-[16px] cursor-pointer ${isDark ? 'text-white' : 'text-[#072837]'}`}>
                            {inbox.name} <span className={`text-[16px] ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}>({inbox.send_as})</span>
                          </label>
                        </div>

                        {/* Channel dropdown for selected inbox */}
                        {formData.inbox_id === inbox.id && (
                          <div className="w-full">
                            <div className={labelClass}>Select Email</div>
                            <div className="relative mt-[4px]" ref={channelSuggestionsRef}>
                              <input
                                className={channelInputClass}
                                type="text"
                                placeholder="Select Email"
                                value={channelInput}
                                disabled={isSubmitting || !formData.email}
                                onChange={handleInboxChannelInputChange}
                                onFocus={() => {
                                  if (!channelInput) {
                                    const opts = inboxChannels.filter(c => c.send_as);
                                    setChannelSuggestions(opts);
                                    setShowChannelSuggestions(opts.length > 0);
                                  } else {
                                    setShowChannelSuggestions(true);
                                  }
                                }}
                                onKeyDown={handleChannelInputKeyDown}
                                autoComplete="off"
                              />
                              <span
                                className="absolute right-0 top-0 h-full flex items-center px-[12px] cursor-pointer z-[2]"
                                style={{ transform: `rotate(${showChannelSuggestions ? '180deg' : '0deg'})` }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  if (showChannelSuggestions) {
                                    setShowChannelSuggestions(false);
                                  } else {
                                    const opts = inboxChannels.filter(c => c.send_as);
                                    setChannelSuggestions(opts);
                                    setShowChannelSuggestions(opts.length > 0);
                                  }
                                }}
                              >
                                {isLoadingChannels
                                  ? <Loader2 className={`h-[18px] w-[18px] animate-spin ${isDark ? 'text-[#b3b3b3]' : 'text-[#555]'}`} />
                                  : <ChevronDown className={`h-[18px] w-[18px] ${isDark ? 'text-white' : 'text-[#000]'}`} />
                                }
                              </span>
                              {showChannelSuggestions && channelSuggestions.length > 0 && (
                                <div className={suggestionListClass}>
                                  {channelSuggestions.map((ch, idx) => (
                                    <div
                                      key={ch.id}
                                      className={`group px-3 py-2 text-[16px] cursor-pointer transition-colors duration-200 ${idx === selectedChannelSuggestionIndex
                                          ? isDark ? 'bg-[#cd6d2c] text-[#000]' : 'bg-[#e9ecef] text-[#072837]'
                                          : isDark ? 'bg-[#161515] hover:bg-[#292929] text-white' : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                                        }`}
                                      onMouseDown={() => handleChannelSuggestionClick(ch)}
                                    >
                                      {ch.name} <span className={`${isDark ? 'text-[#b3b3b3]' : 'text-[#686868] group-hover:!text-white'}`}>({ch.send_as})</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Default channel selection */}
            {!isDefaultChannel && (
              <div className="flex flex-col gap-[12px]">
                <div className={labelClass}>Select Email</div>
                <div className="relative" ref={channelSuggestionsRef}>
                  <input
                    className={channelInputClass}
                    type="text"
                    placeholder={allChannels.length > 0 ? 'Select Email' : 'No Email Found'}
                    value={channelInput}
                    disabled={isSubmitting || !formData.email}
                    onChange={handleChannelInputChange}
                    onFocus={() => {
                      if (!channelInput) {
                        const opts = allChannels.filter(c => c.send_as);
                        setChannelSuggestions(opts);
                        setShowChannelSuggestions(opts.length > 0);
                      } else {
                        setShowChannelSuggestions(true);
                      }
                    }}
                    onKeyDown={handleChannelInputKeyDown}
                    autoComplete="off"
                  />
                  <span
                    className="absolute right-0 top-0 h-full flex items-center px-[12px] cursor-pointer z-[2]"
                    style={{ transform: `rotate(${showChannelSuggestions ? '180deg' : '0deg'})` }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (showChannelSuggestions) {
                        setShowChannelSuggestions(false);
                      } else {
                        const opts = allChannels.filter(c => c.send_as);
                        setChannelSuggestions(opts);
                        setShowChannelSuggestions(opts.length > 0);
                      }
                    }}
                  >
                    {isLoadingChannels
                      ? <Loader2 className={`h-[18px] w-[18px] animate-spin ${isDark ? 'text-[#b3b3b3]' : 'text-[#555]'}`} />
                      : <ChevronDown className={`h-[18px] w-[18px] ${isDark ? 'text-white' : 'text-[#000]'}`} />
                    }
                  </span>
                  {showChannelSuggestions && channelSuggestions.length > 0 && (
                    <div className={suggestionListClass}>
                      {channelSuggestions.map((ch, idx) => (
                        <div
                          key={ch.id}
                          className={`group px-3 py-2 text-[16px] cursor-pointer transition-colors duration-200 ${idx === selectedChannelSuggestionIndex
                              ? isDark ? 'bg-[#cd6d2c] text-[#000]' : 'bg-[#e9ecef] text-[#072837]'
                              : isDark ? 'bg-[#161515] hover:bg-[#292929] text-white' : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                            }`}
                          onMouseDown={() => handleChannelSuggestionClick(ch)}
                        >
                          {ch.name} <span className={`${isDark ? 'text-[#b3b3b3]' : 'text-[#686868] group-hover:!text-white'}`}>({ch.send_as})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            </>}
          </div>
        </div>

        <div className={`sticky bottom-0 z-[10] flex items-center justify-start gap-[12px] px-[24px] py-[20px] rounded-b-[16px] ${isDark ? 'bg-[#000000]' : 'bg-white'}`}>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.email || !formData.channel_id}
            className={`px-[20px] py-[10px] rounded-[10px] text-[16px] font-semibold border-0 cursor-pointer transition-colors duration-200 flex items-center gap-[8px] disabled:opacity-50 disabled:cursor-not-allowed bg-[#cd6d2c] ${isDark ? 'text-[#161515] hover:bg-[#ffffff] hover:text-[#161515]' : 'text-white hover:bg-[#072837] hover:text-white'}`}
          >
            {isSubmitting && <Loader2 className="h-[16px] w-[16px] animate-spin" />}
            {isSubmitting ? 'Saving...' : 'Save Reassignment'}
          </button>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className={`px-[20px] py-[10px] rounded-[10px] text-[16px] font-medium border-0 cursor-pointer transition-colors duration-200 ${isDark ? 'bg-[#292929] text-white hover:bg-[#3a3a3a]' : 'bg-[#e9e9e9] text-[#072837] hover:bg-[#d9d9d9]'}`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReassignModal;